import { useState } from 'react'
import { useSnapshot } from 'valtio'
import {
  MultitipDetailed,
  TipDetailed,
  TipsTypes,
  TypesOfContent,
  useSaveTipMutation,
  useUnsaveTipMutation,
} from '@app/graphql'
import {
  CurrentUserState,
  getPrivateChannelContentEntity,
  getPublicChannelContentEntity,
  PrivateChannelState,
  PublicChannelState,
  updatePrivateChannelEntity,
  updatePublicChannelEntity,
} from '@app/storage'
import { PosthogEvent } from '@app/constants'
import { useAutoVerifyTipsters } from '@app/hooks/useAutoVerifyTipsters'
import { captureAnalytics } from '@app/helpers/misc'

interface UseSaveTipParams {
  id: string
  type: TypesOfContent
  saved: boolean
  multiple?: boolean
  createdAt?: Date
  sportIds?: string[]
}

export const useSaveTip = ({ id, type, saved, multiple, createdAt, sportIds }: UseSaveTipParams) => {
  const [ savedStatus, setSavedStatus ] = useState(saved)
  const { isPublic } = useSnapshot(PublicChannelState)
  const { id: privateChannelId } = useSnapshot(PrivateChannelState)
  const { publicChannelId } = useAutoVerifyTipsters()
  const PUBLIC_CHANNEL_ID = publicChannelId ? String(publicChannelId) : privateChannelId
  const [ saveTip ] = useSaveTipMutation()
  const [ unsaveTip ] = useUnsaveTipMutation()

  const parseType = (t: TypesOfContent) => {
    switch (t) {
      case TypesOfContent.Multitip:
        return TipsTypes.Multitip
      default:
        return TipsTypes.Tip
    }
  }

  const getTipToUpdate = () => {
    let tipToUpdate: TipDetailed | MultitipDetailed = null

    tipToUpdate = multiple
      ? getPublicChannelContentEntity(id, type) as MultitipDetailed
      : getPublicChannelContentEntity(id, type) as TipDetailed

    if (!tipToUpdate) {
      multiple
        ? getPrivateChannelContentEntity(id, type) as MultitipDetailed
        : getPrivateChannelContentEntity(id, type) as TipDetailed
    }
    return tipToUpdate
  }

  const updateTipData = (tipToUpdate: TipDetailed | MultitipDetailed, status: boolean) => {
    if (isPublic) {
      tipToUpdate.isSaved = status
      updatePublicChannelEntity(tipToUpdate)

      return
    }

    tipToUpdate.isSaved = status
    updatePrivateChannelEntity(tipToUpdate)
  }

  const onSaveClick = () => {
    const tipToUpdate = getTipToUpdate()

    if ((tipToUpdate && !tipToUpdate?.isSaved) || !savedStatus) {
      const input = {
        addedFromChannelId: isPublic ? PUBLIC_CHANNEL_ID : privateChannelId,
        contentId: id,
        contentType: parseType(type),
        tipCreatedAt: createdAt?.toISOString() || null,
        tipSportIds: sportIds?.filter(id => id != null) || [],
      }
      saveTip({ variables: input }).then(() => {
        setSavedStatus(true)
        tipToUpdate && updateTipData(tipToUpdate, true)

        captureAnalytics(PosthogEvent.SAVE_TIP, {
          userId: CurrentUserState.id,
          content: tipToUpdate,
        })
      })
      return
    }

    unsaveTip({ variables: { contentId: id, contentType: parseType(type) } }).then(() => {
      setSavedStatus(false)
      tipToUpdate && updateTipData(tipToUpdate, false)
    })
  }

  return { savedStatus, onSaveClick }
}
